import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { getPriceRangeString } from 'site-modules/shared/utils/price-utils';
import { getRangeString } from 'site-modules/shared/utils/string';
import { Link } from 'site-modules/shared/components/link/link';
import { DetailsField } from './details-field';

export function CardDetails({
  children = null,
  showEvData = false,
  uniqueId,
  vehicle: {
    note,
    modelYear,
    priceRange,
    mpgRange,
    edmundsElectricityRange,
    maxEPAElectricityRange,
    modelDisplayOverride,
  },
  isLargeCard = false,
  descriptionId = null,
}) {
  const hasPriceRange = !!(priceRange && priceRange.min && priceRange.max);
  const hasMpgRange = !!(mpgRange && mpgRange.min && mpgRange.max);
  const { makeName, modelName } = modelYear;
  const commonDtClass = 'fw-normal text-cool-gray-30';
  const commonEvProps = {
    id: uniqueId,
    customClass: 'justify-content-between',
    dtClass: classnames('fw-normal text-turquoise-05', {
      'size-16': isLargeCard,
      'size-14': !isLargeCard,
    }),
    ddClass: classnames('text-cool-gray-10', {
      'size-20 fw-medium': isLargeCard,
      'size-16 fw-medium': !isLargeCard,
    }),
  };

  const spacer = <div className="mb-0_25" style={{ height: '24px' }} />;

  let evDetailField = edmundsElectricityRange ? (
    <DetailsField
      {...commonEvProps}
      name="Edmunds EV Range"
      value={`${edmundsElectricityRange} mi`}
      tooltipText={
        <Fragment>
          Edmunds&apos; real-world range test measures the distance an electric vehicle can travel on a full charge.{' '}
          <Link className="text-primary-darker" to="/car-news/electric-car-range-and-consumption-epa-vs-edmunds.html">
            More info
            <span className="ms-0_25 xsmall icon-arrow-right3" aria-hidden />
          </Link>
        </Fragment>
      }
    />
  ) : (
    <DetailsField {...commonEvProps} name="EPA Range" value={`${maxEPAElectricityRange} mi`} />
  );

  if (!edmundsElectricityRange && !maxEPAElectricityRange) {
    evDetailField = spacer;
  }

  const mpgField = hasMpgRange ? (
    <DetailsField
      name="Combined MPG"
      value={getRangeString(mpgRange.min, mpgRange.max)}
      id={uniqueId}
      customClass="justify-content-between"
      dtClass={classnames(commonDtClass, {
        'size-16': isLargeCard,
        'size-14': !isLargeCard,
      })}
      ddClass={classnames('text-cool-gray-10', {
        'size-20 fw-medium': isLargeCard,
        'size-16 fw-medium': !isLargeCard,
      })}
    />
  ) : (
    spacer
  );

  const srOnlyMakeModelName = `${makeName} ${modelDisplayOverride || modelName}`;

  const noteWithCoreLink = (
    <div id={descriptionId}>
      <span
        className="text-cool-gray-30 mb-0_5"
        style={
          isLargeCard
            ? {
                overflow: 'hidden',
                minHeight: '5rem',
                display: '-webkit-box',
                WebkitLineClamp: '4',
                WebkitBoxOrient: 'vertical',
              }
            : null
        }
      >
        {`${note} `}
      </span>
      <span className="text-blue-50 text-underline" aria-hidden>
        Read full review
      </span>
    </div>
  );

  return (
    <Fragment>
      {isLargeCard ? (
        noteWithCoreLink
      ) : (
        <details className="make-model-description text-cool-gray-30">
          <summary
            className="size-14 text-blue-50 ms-auto w-50 ps-0_5 list-unstyled pos-r"
            style={{ marginTop: '-1rem' }}
          >
            <span className="visually-hidden">{`About ${srOnlyMakeModelName}`}</span>
            <span className="read-summary">
              Read Summary&nbsp;
              <span className="size-12 icon-arrow-down4" aria-hidden />
            </span>
            <span className="close-summary">
              Close Summary&nbsp;
              <span className="size-12 icon-arrow-up4" aria-hidden />
            </span>
          </summary>
          <div className="pt-0_75">{noteWithCoreLink}</div>
        </details>
      )}

      <div className="details-col flex-fill">
        <hr className="my-1 w-100" />
        {hasPriceRange ? (
          <DetailsField
            name="MSRP"
            value={getPriceRangeString(priceRange.min, priceRange.max)}
            id={uniqueId}
            customClass="justify-content-between"
            dtClass={classnames(commonDtClass, {
              'size-16': isLargeCard,
              'size-14': !isLargeCard,
            })}
            ddClass={classnames('fw-medium text-cool-gray-10', {
              'size-20': isLargeCard,
            })}
          />
        ) : (
          spacer
        )}

        {showEvData ? evDetailField : mpgField}
      </div>
      {children}
    </Fragment>
  );
}

CardDetails.propTypes = {
  vehicle: TypeRankingsEntities.RankingVehicle.isRequired,
  showEvData: PropTypes.bool,
  uniqueId: PropTypes.string.isRequired,
  isLargeCard: PropTypes.bool,
  children: PropTypes.node,
  descriptionId: PropTypes.string,
  coreLink: PropTypes.string,
};
